import { renewalInterest } from "./renewalInterest.ts";
import { PHASE, PLAYER } from "../../../common/constants.ts";
import { g, helpers } from "../../util/index.ts";
import { idb } from "../../db/index.ts";
import moodComponents from "./moodComponents.ts";
import type { Player } from "../../../common/types.ts";
import { bySport, isSport } from "../../../common/sportFunctions.ts";
import { last } from "../../../common/utils.ts";
import { uniformSeed } from "../../../common/random.ts";
import { MIN_CONTRACT_AGE } from "./setContract.ts";
import {
	MAX_CONTRACT_DISCOUNT_PER_TRAIT,
	postedCap,
	roomForMaxContract,
	traitsSatisfied,
} from "../team/maxContracts.baseball.ts";

const hasActiveNegotiation = async (tid: number, pid: number) => {
	return (await idb.cache.negotiations.getAll()).some(
		(negotiation) => negotiation.tid === tid && negotiation.pid === pid,
	);
};

const moodInfo = async (
	p: Player,
	tid: number,
	overrides: {
		contractAmount?: number;
		salaryCoverage?: number;
	} = {},
) => {
	const components = await moodComponents(p, tid);
	let probWilling = 0;

	const phase = g.get("phase");
	const season = g.get("season");

	const resigning = phase === PHASE.RESIGN_PLAYERS;
	const rookie =
		phase >= PHASE.DRAFT &&
		phase <= PHASE.RESIGN_PLAYERS &&
		p.draft.year === season;

	let firstSeasonAfterExpansionOverride = false;
	if (
		(p.contract.exp === season &&
			phase >= PHASE.PLAYOFFS &&
			phase <= PHASE.RESIGN_PLAYERS) ||
		(phase === PHASE.RESIGN_PLAYERS && p.tid === PLAYER.FREE_AGENT)
	) {
		const t = await idb.cache.teams.get(tid);
		if (
			t &&
			t.firstSeasonAfterExpansion !== undefined &&
			t.firstSeasonAfterExpansion - 1 === season
		) {
			firstSeasonAfterExpansionOverride = true;
		}
	}

	let sumComponents = 0;
	for (const key of helpers.keys(components)) {
		if (key === "custom") {
			for (const row of components.custom!) {
				sumComponents += row.amount;
			}
		} else {
			sumComponents += components[key];
		}
	}

	// Add some based on how long free agency has lasted and how good/bad the player is
	let sumAndStuff = sumComponents - 0.5;
	if (p.tid === PLAYER.FREE_AGENT) {
		sumAndStuff += helpers.bound(p.numDaysFreeAgent, 0, 30) / 3;
	}

	let valueDiff =
		(p.value -
			bySport({ baseball: 75, basketball: 65, football: 85, hockey: 75 })) /
		2;

	// It's annoying to root against your player improving, but you do that for very good players sometimes. This prevents that from happening by capping valueDiff, but only when you're re-signing your own player
	const MAX_RESIGNING_VALUE_DIFF = 4;
	if (valueDiff > MAX_RESIGNING_VALUE_DIFF) {
		// Is player really re-signing? Otherwise do nothing.
		if (
			p.tid === tid || (isSport("baseball") && p.tid === PLAYER.MINORS && p.minorTid === tid) ||
			(resigning &&
				p.tid === PLAYER.FREE_AGENT &&
				(await hasActiveNegotiation(tid, p.pid)))
		) {
			valueDiff = MAX_RESIGNING_VALUE_DIFF;
		}
	}

	sumAndStuff -= valueDiff > 0 ? Math.sqrt(valueDiff) : valueDiff;

	const thisIsAUserTeam = g.get("userTids").includes(tid);

	// More AI players testing free agency
	if (!thisIsAUserTeam) {
		sumAndStuff -= 3;
	}

	let contractAmount = overrides.contractAmount ?? p.contract.amount;

	// Up to 50% penalty for bad mood, except if this is a rookie contract
	const autoRookieContract =
		components.rookieContract > 0 && g.get("draftPickAutoContract");
	if (!autoRookieContract && contractAmount > g.get("minContract")) {
		contractAmount *= helpers.bound(1 + (0.5 * -sumComponents) / 10, 1, 1.5);
	}

	const traits = g.get("playerMoodTraits") ? p.moodTraits : [];

	// A player good enough for the maximum contract gives any club he likes a discount, 12.5% for
	// each of his traits it satisfies: market and hype (F), winning (W), his own club (L). Money-driven
	// players give none. This replaces the usual discount to stay below.
	const maxEligible =
		isSport("baseball") &&
		!autoRookieContract &&
		(overrides.contractAmount ?? p.contract.amount) >= g.get("maxContract");
	if (maxEligible && !traits.includes("$")) {
		contractAmount =
			Math.min(contractAmount, g.get("maxContract")) *
			(1 -
				MAX_CONTRACT_DISCOUNT_PER_TRAIT *
					traitsSatisfied(traits, components));

		// A club already carrying five maximum contracts can't pay him the maximum, so he takes less
		// to play there. Only a money-driven player (no discount at all) says no.
		if (
			!roomForMaxContract(
				await idb.cache.players.indexGetAll("playersByTid", tid),
				contractAmount,
				p.pid,
			)
		) {
			contractAmount =
				g.get("maxContract") * (1 - MAX_CONTRACT_DISCOUNT_PER_TRAIT);
		}
	}

	// A happy player gives his own team a discount to stay. Loyal ones give more, money-driven
	// ones barely any. Only for re-signing, never for a new team.
	const ownTeam =
		p.tid === tid || (isSport("baseball") && p.tid === PLAYER.MINORS && p.minorTid === tid) ||
		(resigning &&
			p.tid === PLAYER.FREE_AGENT &&
			(await hasActiveNegotiation(tid, p.pid)));
	if (
		isSport("baseball") &&
		ownTeam &&
		!(maxEligible && !traits.includes("$")) &&
		!autoRookieContract &&
		sumComponents > 0 &&
		contractAmount > g.get("minContract")
	) {
		let discount = 0.02 * sumComponents;
		if (traits.includes("L")) {
			discount *= 1.5;
		}
		if (traits.includes("$")) {
			discount *= 0.3;
		}
		contractAmount *= 1 - helpers.bound(discount, 0, 0.25);
	}

	contractAmount = Math.min(contractAmount, postedCap(p) ?? Infinity);

	contractAmount = helpers.bound(
		helpers.roundContract(contractAmount),
		g.get("minContract"),
		g.get("maxContract"),
	);

	// Future intent excludes automatic rookie rights. Those remain separate rules below.
 const interestScore = sumAndStuff - (isSport("baseball") ? components.rookieContract : 0);
 const renewal = renewalInterest(interestScore, overrides.salaryCoverage ?? 1,
  g.get("playerMoodTraits") && p.moodTraits.includes("$"));
 let willing = false;
	// Under 21 a player can't refuse to stay with his club, whatever his playing time
	const youngOwnPlayer =
		isSport("baseball") && ownTeam && season - p.born.year < MIN_CONTRACT_AGE;

	if (
		!g.get("playersRefuseToNegotiate") ||
		rookie ||
		youngOwnPlayer ||
		firstSeasonAfterExpansionOverride ||
		(contractAmount === g.get("minContract") && p.tid === PLAYER.FREE_AGENT)
	) {
		probWilling = 1;
		willing = true;
	} else if (components.rookieContract > 0 && !g.get("rookiesCanRefuse")) {
		probWilling = 1;
		willing = true;
	} else {
		// Decrease that 0.7 to make players less likely to be at extremes (1% or 99%) in mood
		probWilling = isSport("baseball") ? renewal.interest : 1 / (1 + Math.exp(-0.7 * sumAndStuff));

		let seed =
			tid +
			p.pid +
			p.stats.length +
			last(p.ratings).ovr +
			(p.stats.at(-1)?.min ?? 0);

		if (isSport("baseball")) {
			// Since min is 0 in baseball
			seed += (p.stats.at(-1)?.pa ?? 0) + (p.stats.at(-1)?.outs ?? 0);
		}

		const rand = uniformSeed(seed);
		willing = rand < probWilling;
	}

	// Outside the above if/else so it plays nice with any branch
	if (
		g.get("challengeNoFreeAgents") &&
		!resigning &&
		contractAmount * 0.99 > g.get("minContract")
	) {
		willing = false;
	}

	return {
		renewalInterest: renewal.interest,
		interestScore,
		financialInterest: renewal.financial,
		components,
		traits: g.get("playerMoodTraits") ? p.moodTraits : [],
		probWilling,
		willing,
		contractAmount,
	};
};

export default moodInfo;
